import React, { useId, useState } from "react";
import {
  sumPropertyMoney,
  normalizePropertyMoney,
} from "../../utils/propertyLoanMoney";

const currency = (amount) =>
  Number(amount || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "SGD",
  });

export default function PropertyLoanRepaymentSchedule({ schedule = [] }) {
  const titleId = useId();
  const [expanded, setExpanded] = useState(false);
  const hiddenMonths = Math.max(0, schedule.length - 6);
  const totalPrincipal = sumPropertyMoney(
    ...schedule.map((row) => row.principal),
  );
  const totalInterest = sumPropertyMoney(
    ...schedule.map((row) => row.interest),
  );
  const totalPayments = sumPropertyMoney(...schedule.map((row) => row.total));
  const lastPayment = schedule[schedule.length - 1];
  const closingBalance = lastPayment
    ? Math.max(
        0,
        normalizePropertyMoney(
          Number(lastPayment.loanBalance || 0) -
            Number(lastPayment.principal || 0),
        ),
      )
    : 0;

  return (
    <section
      className="property-term-schedule-preview"
      aria-labelledby={titleId}
    >
      <div className="property-term-schedule-heading">
        <div>
          <h3 id={titleId}>Loan repayment schedule</h3>
          <p>
            {hiddenMonths > 0 && !expanded
              ? "Showing the first and last 3 months of your repayments."
              : "Your monthly repayment breakdown."}
          </p>
        </div>
        <span className="property-term-schedule-tenor">
          {schedule.length} months
        </span>
      </div>
      <div
        className="property-term-schedule-scroll"
        role="region"
        aria-label="Repayment breakdown"
        tabIndex={0}
      >
        <table aria-labelledby={titleId}>
          <thead>
            <tr>
              {["Month", "Principal", "Interest", "Total", "Loan Balance"].map(
                (title) => (
                  <th
                    key={title}
                    scope="col"
                    className={
                      title === "Total"
                        ? "property-term-schedule-total"
                        : undefined
                    }
                  >
                    {title}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {schedule.map((repay, index) => (
              <React.Fragment key={index}>
                {hiddenMonths > 0 && index === 3 && (
                  <tr>
                    <td colSpan={5} className="property-term-schedule-toggle">
                      <button
                        type="button"
                        aria-expanded={expanded}
                        onClick={() => setExpanded(!expanded)}
                      >
                        {expanded
                          ? `Hide ${hiddenMonths} middle months`
                          : `Show ${hiddenMonths} middle months`}
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          aria-hidden="true"
                        >
                          <path
                            d={expanded ? "m6 15 6-6 6 6" : "m6 9 6 6 6-6"}
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </td>
                  </tr>
                )}
                {(expanded || index < 3 || index >= schedule.length - 3) && (
                  <tr className="property-term-schedule-payment">
                    <th scope="row">
                      <span
                        className="property-term-schedule-month-number"
                        aria-hidden="true"
                      >
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      {repay.month}
                    </th>
                    <td>{currency(repay.principal)}</td>
                    <td>{currency(repay.interest)}</td>
                    <td className="property-term-schedule-total">
                      {currency(repay.total)}
                    </td>
                    <td>{currency(repay.loanBalance)}</td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
          {schedule.length > 0 && (
            <tfoot>
              <tr className="property-term-schedule-totals">
                <th scope="row">Total</th>
                <td>{currency(totalPrincipal)}</td>
                <td>{currency(totalInterest)}</td>
                <td className="property-term-schedule-total">
                  {currency(totalPayments)}
                </td>
                <td aria-label="Closing loan balance">
                  {currency(closingBalance)}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </section>
  );
}
